function themeElement() {
  if (document.body?.getAttribute("data-bs-theme")) return document.body
  return document.documentElement
}

export function readChartTheme() {
  const el = themeElement()
  const style = getComputedStyle(el)
  const isDark = el.getAttribute("data-bs-theme") === "dark"

  const bodyBg = style.getPropertyValue("--bs-body-bg").trim() || (isDark ? "#212529" : "#ffffff")
  const bodyColor = style.getPropertyValue("--bs-body-color").trim() || (isDark ? "#dee2e6" : "#212529")
  const secondaryColor = style.getPropertyValue("--bs-secondary-color").trim() || (isDark ? "#adb5bd" : "#6c757d")
  const borderColor = style.getPropertyValue("--bs-border-color").trim() || (isDark ? "#495057" : "#dee2e6")
  const tertiaryBg = style.getPropertyValue("--bs-tertiary-bg").trim() || (isDark ? "#2b3035" : "#f8f9fa")

  return {
    isDark,
    bodyBg,
    bodyColor,
    secondaryColor,
    borderColor,
    tertiaryBg,
    gridColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.05)",
    axisLabelColor: secondaryColor,
    mapStrokeColor: isDark ? borderColor : "#ffffff",
    mapNullColor: isDark ? tertiaryBg : "#f8f9fa",
    barSeparatorColor: isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.18)"
  }
}

export function apexTooltipOptions(theme = readChartTheme()) {
  return {
    theme: theme.isDark ? "dark" : "light",
    style: {
      fontSize: "12px",
      fontFamily: "inherit"
    }
  }
}

function parseHex(hex) {
  const normalized = String(hex || "#198754").replace("#", "")
  const full = normalized.length === 3
    ? normalized.split("").map((char) => char + char).join("")
    : normalized.padEnd(6, "0").slice(0, 6)

  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16)
  }
}

function toHex(r, g, b) {
  return `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, "0")).join("")}`
}

function mixWithWhite(hex, amount = 0.8) {
  const { r, g, b } = parseHex(hex)
  const mix = (channel) => Math.round(channel + (255 - channel) * amount)
  return toHex(mix(r), mix(g), mix(b))
}

function shadeHex(hex, factor = -0.1) {
  const { r, g, b } = parseHex(hex)
  const shade = (channel) => {
    const value = factor < 0 ? channel * (1 + factor) : channel + (255 - channel) * factor
    return Math.max(0, Math.min(255, Math.round(value)))
  }
  return toHex(shade(r), shade(g), shade(b))
}

export function apexDataLabelPill(accentColor, theme = readChartTheme()) {
  const backgroundColor = theme.isDark ? theme.tertiaryBg : mixWithWhite(accentColor, 0.82)
  const foreColor = theme.isDark ? accentColor : shadeHex(accentColor, -0.12)

  return {
    textColor: foreColor,
    background: {
      enabled: true,
      backgroundColor,
      foreColor,
      padding: 4,
      borderRadius: 6,
      borderWidth: 0,
      opacity: 1,
      dropShadow: { enabled: false }
    }
  }
}

export function observeChartTheme(callback) {
  const handler = () => callback()
  document.addEventListener("depozen:theme-changed", handler)
  return () => document.removeEventListener("depozen:theme-changed", handler)
}

// Matches dashboard masonry / chart layout (`lg` in this project).
export const COMPACT_CHART_MAX_WIDTH = 1027

export function isCompactChartViewport() {
  if (typeof window === "undefined" || !window.matchMedia) return false
  return window.matchMedia(`(max-width: ${COMPACT_CHART_MAX_WIDTH}px)`).matches
}

export function apexChartGridPadding(desktop, compactOverrides = {}) {
  if (!isCompactChartViewport()) return desktop
  return { ...desktop, ...compactOverrides }
}

export function observeCompactChartViewport(callback) {
  if (typeof window === "undefined" || !window.matchMedia) return () => {}

  const mediaQuery = window.matchMedia(`(max-width: ${COMPACT_CHART_MAX_WIDTH}px)`)
  const handler = () => callback()

  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener("change", handler)
    return () => mediaQuery.removeEventListener("change", handler)
  }

  mediaQuery.addListener(handler)
  return () => mediaQuery.removeListener(handler)
}
